import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { isSsr } from '@99designs/browser';
import { getSavingsAmountInCents } from '@99designs/feature-product-details';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import {
    convertUSDToLocalisedAmountInCents,
    roundAmountInCentsToNearestNine,
    roundAmountInCentsToWholeAmount,
} from '../helpers/currencyHelpers';
import { getRecommendedProducts } from './utils/getRecommendedProducts';
import type { ProductPackageDetails } from '@99designs/graph-utils/types';
import type { RecommendedBrandingProductsQuery } from './graphql/Recommendation.generated';
import type { Product } from './types';

export function useProductConfig(
    recommendedProducts: Product[],
    currency: CurrencyCode,
    exchangeRate: number
) {
    const intl = useIntl();
    const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;

    const getFormattedPrice = (amount: number, currency: string) => {
        return intl.formatNumber(amount, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
        });
    };

    const populateProducts = (
        data: RecommendedBrandingProductsQuery,
        recommendedProducts: Product[],
        exchangeRate: number
    ) => {
        return recommendedProducts.map((recommendedProduct: Product) => {
            const matchedProduct = data.products.find(
                (product) => product.marketing?.key === recommendedProduct.marketingKey
            );

            const product = { ...recommendedProduct };

            if (product.marketingKey === 'logo-website-squarespace') {
                product.price = {
                    currency: CurrencyCode.USD,
                    amountInCents: 1_399_00,
                };
            }

            if (!matchedProduct) {
                return product;
            }

            switch (matchedProduct.__typename) {
                case 'ProductFixedPriceProject': {
                    const { pricing, title, description, details } = matchedProduct;

                    product.price = {
                        currency: pricing.amount.currency as CurrencyCode,
                        amountInCents: pricing.amount.amountInCents,
                    };

                    if (product.marketingKey === 'facebook-creative') {
                        return product;
                    }

                    product.title = title;
                    product.description = description;
                    product.deliverables = details.map(
                        (detail: ProductPackageDetails) => detail.title
                    );

                    break;
                }
                case 'ProductContest': {
                    const { pricing } = matchedProduct;
                    product.price = {
                        currency: pricing.amount.currency as CurrencyCode,
                        amountInCents: roundAmountInCentsToNearestNine(
                            pricing.amount.amountInCents,
                            currency
                        ),
                    };

                    const savingsAmount = product.marketingKey
                        ? getSavingsAmountInCents(product.marketingKey, currency)
                        : undefined;

                    if (savingsAmount) {
                        product.saving = formatMessage(__('Save {price}'), {
                            price:
                                typeof savingsAmount === 'number'
                                    ? getFormattedPrice(
                                          roundAmountInCentsToWholeAmount(savingsAmount, currency),
                                          currency
                                      )
                                    : savingsAmount,
                        });
                    }

                    break;
                }
                case 'ProductQuotedProject': {
                    const { priceRange, title, description, quotedProjectDetails } = matchedProduct;

                    if (priceRange.fromPrice && currency === 'USD') {
                        product.price = {
                            currency,
                            amountInCents: roundAmountInCentsToNearestNine(
                                priceRange.fromPrice,
                                currency
                            ),
                        };
                    }

                    if (priceRange.fromPrice && currency !== 'USD') {
                        const localisedAmount = convertUSDToLocalisedAmountInCents(
                            priceRange.fromPrice,
                            {
                                currency,
                                rate: exchangeRate,
                            }
                        );

                        product.price = {
                            currency,
                            amountInCents: roundAmountInCentsToNearestNine(
                                localisedAmount.amountInCents,
                                currency
                            ),
                        };
                    }

                    product.title = title;
                    product.description = description;
                    product.deliverables = quotedProjectDetails;

                    break;
                }
            }

            return product;
        });
    };

    const [productsLoading, setProductsLoading] = useState<boolean>(false);
    const [products, setProducts] = useState<Product[]>([]);
    const names = recommendedProducts
        .map(({ productName }) => (productName ? productName : ''))
        .filter(Boolean);

    const fetchRecommendedProducts = async (names: string[]) => {
        setProductsLoading(true);

        const data = await getRecommendedProducts(apolloClient, names);

        setProducts(populateProducts(data, recommendedProducts, exchangeRate));
        setProductsLoading(false);
    };

    if (!isSsr() && !productsLoading && products.length === 0) {
        fetchRecommendedProducts(names);
    }

    return products;
}
